import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useRef,
	useState,
} from "react";
import ConfirmDialog, {
	type ConfirmDialogOptions,
} from "../components/ConfirmDialog";

interface ConfirmDialogContextValue {
	confirm: (options: ConfirmDialogOptions) => Promise<boolean>;
}

const ConfirmDialogContext = createContext<
	ConfirmDialogContextValue | undefined
>(undefined);

export const ConfirmDialogProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const resolveRef = useRef<(value: boolean) => void>(() => {});
	const [state, setState] = useState<{
		open: boolean;
		options: ConfirmDialogOptions | null;
	}>({
		open: false,
		options: null,
	});

	const confirm = useCallback((options: ConfirmDialogOptions) => {
		return new Promise<boolean>((resolve) => {
			resolveRef.current = resolve;
			setState({ open: true, options });
		});
	}, []);

	const handleConfirm = useCallback(() => {
		resolveRef.current(true);
		setState({ open: false, options: null });
	}, []);

	const handleCancel = useCallback(() => {
		resolveRef.current(false);
		setState({ open: false, options: null });
	}, []);

	return (
		<ConfirmDialogContext.Provider value={{ confirm }}>
			{children}
			{state.options && (
				<ConfirmDialog
					open={state.open}
					title={state.options.title}
					message={state.options.message}
					confirmLabel={state.options.confirmLabel}
					variant={state.options.variant}
					onConfirm={handleConfirm}
					onCancel={handleCancel}
				/>
			)}
		</ConfirmDialogContext.Provider>
	);
};

export default function useConfirmDialog(): ConfirmDialogContextValue {
	const context = useContext(ConfirmDialogContext);
	if (!context) {
		throw new Error(
			"useConfirmDialog must be used within ConfirmDialogProvider",
		);
	}
	return context;
}
